<template>
    <div class="field-availability-table">
        <h4 class="mb-3">Disponibilità Campi per Orario</h4>
        
        <!-- Day Selector -->
        <div class="day-selector mb-4">
            <div 
                v-for="day in daysOfWeek" 
                :key="day.value"
                :class="['day-box', { 'active': selectedDay === day.value }]"
                @click="selectDay(day.value)"
            >
                <div class="day-name">{{ day.short }}</div>
                <div class="day-date">{{ day.date }}</div>
            </div>
        </div>
        
        <div class="table-responsive">
            <DataTable :value="timeSlots" class="p-datatable-sm p-datatable-gridlines" :scrollable="true" scrollHeight="600px">
                <Column field="time" header="Orario" :frozen="true" style="min-width: 100px; font-weight: bold;">
                    <template #body="slotProps">
                        {{ slotProps.data.time }}
                    </template>
                </Column>
                <Column v-for="field in fieldsList" :key="field.id" :header="field.name" style="min-width: 150px; text-align: center;">
                    <template #body="slotProps">
                        <div :class="getCellClass(slotProps.data, field)" class="availability-cell">
                            {{ getCellContent(slotProps.data, field) }}
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import moment from 'moment';

export default {
    name: 'FieldAvailabilityTable',
    props: {
        fields: {
            type: Array,
            required: true
        },
        selectedDate: {
            type: Date,
            required: true
        },
        selectedDay: {
            type: Number,
            default: null
        }
    },
    emits: ['day-change'],
    setup(props, { emit }) {
        // Generate days of week based on selectedDate
        // Generate days of week based on selectedDate
        const daysOfWeek = computed(() => {
            // Use isoWeek to force Monday as the start of the week
            const startOfWeek = moment(props.selectedDate).startOf('isoWeek');
            const days = [];
            const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
            
            for (let i = 0; i < 7; i++) {
                const day = moment(startOfWeek).add(i, 'days');
                days.push({
                    value: i, // 0 = Monday, ..., 6 = Sunday
                    short: dayNames[i],
                    date: day.format('DD/MM'),
                    fullDate: day.toDate()
                });
            }
            
            return days;
        });
        
        const selectDay = (dayValue) => {
            // Toggle: if clicking the same day, deselect it
            if (props.selectedDay === dayValue) {
                emit('day-change', null);
            } else {
                emit('day-change', dayValue);
            }
        };
        
        // Extract unique fields and filter sessions by selected day
        const fieldsList = computed(() => {
            return props.fields.map(field => {
                let filteredSessions = field.sessions || [];
                
                // Filter sessions by selected day if a day is selected
                if (props.selectedDay !== null) {
                    const targetDate = daysOfWeek.value[props.selectedDay].fullDate;
                    filteredSessions = filteredSessions.filter(session => {
                        const sessionDate = moment(session.start);
                        return sessionDate.isSame(targetDate, 'day');
                    });
                }
                
                return {
                    id: field.id,
                    name: field.name,
                    sessions: filteredSessions
                };
            });
        });

        // Generate time slots (every 30 minutes)
        const timeSlots = computed(() => {
            const slots = [];
            const startHour = 7; // Start at 7:00
            const endHour = 23; // End at 23:00
            
            for (let hour = startHour; hour <= endHour; hour++) {
                for (let minute of [0, 30]) {
                    if (hour === endHour && minute === 30) break; // Don't go past 23:00
                    
                    const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                    const slotStart = moment().hour(hour).minute(minute).second(0);
                    const slotEnd = moment(slotStart).add(30, 'minutes');
                    
                    slots.push({
                        time: timeString,
                        start: slotStart,
                        end: slotEnd
                    });
                }
            }
            
            return slots;
        });

        // Check if a field is occupied at a given time slot
        const getOccupyingSession = (timeSlot, field) => {
            if (!field.sessions || field.sessions.length === 0) return null;
            
            // Extract hour and minute from the time slot
            const slotStartHour = timeSlot.start.hour();
            const slotStartMinute = timeSlot.start.minute();
            const slotEndHour = timeSlot.end.hour();
            const slotEndMinute = timeSlot.end.minute();
            
            // Convert to minutes since midnight for easier comparison
            const slotStartMinutes = slotStartHour * 60 + slotStartMinute;
            const slotEndMinutes = slotEndHour * 60 + slotEndMinute;
            
            for (const session of field.sessions) {
                const sessionStart = moment(session.start);
                const sessionEnd = moment(session.end);
                
                // Extract hour and minute from session
                const sessionStartMinutes = sessionStart.hour() * 60 + sessionStart.minute();
                const sessionEndMinutes = sessionEnd.hour() * 60 + sessionEnd.minute();
                
                // Check if the time slot overlaps with the session (comparing only times, not dates)
                // A slot is occupied if it starts before the session ends and ends after the session starts
                if (slotStartMinutes < sessionEndMinutes && slotEndMinutes > sessionStartMinutes) {
                    return session;
                }
            }
            
            return null;
        };

        const getCellClass = (timeSlot, field) => {
            const session = getOccupyingSession(timeSlot, field);
            return session ? 'occupied' : 'free';
        };

        const getCellContent = (timeSlot, field) => {
            const session = getOccupyingSession(timeSlot, field);
            if (session) {
                return session.title || 'Occupato';
            }
            return 'Libero';
        };

        return {
            daysOfWeek,
            selectDay,
            fieldsList,
            timeSlots,
            getCellClass,
            getCellContent
        };
    }
};
</script>

<style scoped>
.field-availability-table {
    margin-bottom: 2rem;
}

.day-selector {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
}

.day-box {
    flex: 1;
    min-width: 80px;
    max-width: 120px;
    padding: 0.75rem;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #fff;
}

.day-box:hover {
    border-color: #007bff;
    background-color: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.day-box.active {
    background-color: #007bff;
    border-color: #007bff;
    color: white;
}

.day-name {
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
}

.day-date {
    font-size: 0.85rem;
    opacity: 0.8;
}

.table-responsive {
    overflow-x: auto;
}

.availability-cell {
    padding: 0.5rem;
    border-radius: 4px;
    font-weight: 500;
    font-size: 0.85rem;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.free {
    background-color: #d4edda;
    color: #155724;
}

.occupied {
    background-color: #f8d7da;
    color: #721c24;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
    background-color: #f8f9fa;
    font-weight: 600;
    text-align: center;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.25rem;
}
</style>
